/**
 * CustomerAIUpsellPanel.jsx — Iter 168 (Danny)
 *
 * Right-rail AI Upsell panel on the Customer Focus page. Mirrors the
 * visual treatment of LeadAIScorePanel (PRIMARY + chips with FitScore
 * bars), but the engine here is the existing
 * `/api/ai/recommend-products` endpoint — recommendations are
 * filtered to products NOT already enrolled.
 *
 * Props:
 *   - contact: the customer document
 *   - currentProducts: list of strings (product labels already enrolled)
 *   - apiBase, token
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Sparkles, RefreshCw, TrendingUp, Loader2, AlertTriangle } from "lucide-react";

const ALL_PRODUCTS = [
  "AI-powered EHR (MDNet)", "Medical Billing", "RevQ AI Billing",
  "Practice Management", "CCM / APCM / RPM", "REACH", "AWV Program",
  "MIPS Advisory", "Telehealth", "Patient Portal", "Revenue Cycle Management",
  "Care Coordination", "Population Health", "Analytics Dashboard",
];

function FitBar({ pct }) {
  const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div style={{ height: 6, borderRadius: 3, background: "#e5e7eb", overflow: "hidden", marginTop: 6 }}>
      <div style={{ width: `${clamped}%`, height: "100%", background: clamped >= 70 ? "var(--success)" : clamped >= 40 ? "#f59e0b" : "#9ca3af", transition: "width 200ms ease" }} />
    </div>
  );
}

function UpsellChip({ rec, isPrimary, testid }) {
  return (
    <div data-testid={testid} style={{ padding: "12px 14px", background: isPrimary ? "linear-gradient(135deg, #F3E8FF, #EDE9FE)" : "var(--paper)", border: isPrimary ? "1px solid #c4b5fd" : "0.5px solid var(--border)", borderRadius: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: "var(--navy)" }}>{rec.product}</div>
        <div className="num" style={{ fontSize: 14, fontWeight: 700, color: isPrimary ? "#6d28d9" : "var(--text-2)" }}>
          {Math.round(rec.match || 0)}
        </div>
      </div>
      {isPrimary && (
        <div style={{ display: "inline-block", marginTop: 4, fontSize: 9, fontWeight: 700, color: "#6d28d9", letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Best fit
        </div>
      )}
      <FitBar pct={rec.match} />
      {rec.reason && <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 8, lineHeight: 1.5 }}>{rec.reason}</div>}
    </div>
  );
}

export default function CustomerAIUpsellPanel({ contact, currentProducts = [], leadContext = null, apiBase, token }) {
  const [loading, setLoading] = useState(false);
  const [recs, setRecs] = useState(null);
  const [error, setError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  const fetchRecs = useCallback(async () => {
    if (!contact?.id) return;
    setLoading(true); setError(null);
    try {
      const res = await axios.post(`${apiBase}/ai/recommend-products`, {
        specialty: contact?.specialty || leadContext?.specialty || "",
        pain_points: leadContext?.pain_points || [],
        practice_type: contact?.company || "",
        budget: leadContext?.budget_range || "",
      }, { headers: { Authorization: `Bearer ${token}` } });
      const raw = res.data?.recommendations || [];
      // Filter out anything already enrolled, normalise match score.
      const filtered = raw
        .filter(r => !currentProducts.some(cp => (cp || "").toLowerCase().includes((r.product || "").toLowerCase()) || (r.product || "").toLowerCase().includes((cp || "").toLowerCase())))
        .slice(0, 6);
      setRecs(filtered.length ? filtered : getFallback(currentProducts, contact));
      setLastFetchedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || "AI upsell unavailable");
      setRecs(getFallback(currentProducts, contact));
    } finally {
      setLoading(false);
    }
  }, [contact, currentProducts, leadContext, apiBase, token]);

  useEffect(() => { fetchRecs(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [contact?.id]);

  const primary = recs?.[0];
  const secondary = recs?.slice(1, 5) || [];
  const estimatedUpliftMrr = (recs || []).reduce((s, r) => s + (r.estimated_mrr || 0), 0);

  return (
    <div data-testid="customer-ai-upsell-panel" className="card" style={{ padding: 0, borderRadius: "var(--radius-lg)", overflow: "hidden" }}>
      <div style={{ padding: "14px 18px", borderBottom: "0.5px solid var(--border-soft)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Sparkles style={{ width: 14, height: 14, color: "#7c3aed" }} />
          <div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 600, color: "var(--navy)" }}>AI Upsell Score</div>
            <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 1 }}>
              {lastFetchedAt ? `Cached · ${lastFetchedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "—"}
            </div>
          </div>
        </div>
        <button
          data-testid="customer-ai-upsell-rescore"
          onClick={fetchRecs}
          disabled={loading}
          className="btn"
          style={{ fontSize: 10, padding: "4px 10px" }}>
          {loading ? <Loader2 style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} /> : <RefreshCw style={{ width: 11, height: 11 }} />}
          Re-score
        </button>
      </div>

      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        {error && (
          <div style={{ padding: "8px 10px", borderRadius: 6, background: "#fef3c7", border: "0.5px solid #fde68a", fontSize: 11, color: "#92400e", display: "flex", alignItems: "center", gap: 6 }}>
            <AlertTriangle style={{ width: 11, height: 11 }} />
            {error} — showing offline fallback
          </div>
        )}

        {loading && !recs && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text-3)" }}>
            <Loader2 style={{ width: 20, height: 20, animation: "spin 1s linear infinite", color: "#7c3aed" }} />
            <div style={{ fontSize: 11, marginTop: 6 }}>Computing best-fit upsells…</div>
          </div>
        )}

        {!loading && primary && (
          <>
            <UpsellChip rec={primary} isPrimary testid="customer-upsell-primary" />
            {secondary.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
                {secondary.map((r, i) => (
                  <UpsellChip key={r.product || i} rec={r} testid={`customer-upsell-secondary-${i}`} />
                ))}
              </div>
            )}
            {estimatedUpliftMrr > 0 && (
              <div data-testid="customer-upsell-uplift" style={{ marginTop: 4, padding: "10px 12px", borderRadius: 8, background: "var(--success-soft)", border: "0.5px solid #bbf7d0", display: "flex", alignItems: "center", gap: 8 }}>
                <TrendingUp style={{ width: 13, height: 13, color: "var(--success)" }} />
                <div style={{ fontSize: 11, color: "#065f46" }}>
                  Potential MRR uplift: <strong className="num">${estimatedUpliftMrr.toLocaleString()}</strong>/mo
                </div>
              </div>
            )}
          </>
        )}

        {!loading && !primary && (
          <div style={{ fontSize: 11, color: "var(--text-3)", textAlign: "center", padding: "16px 4px", fontStyle: "italic" }}>
            No upsell opportunities — customer is on every recommended product.
          </div>
        )}
      </div>
    </div>
  );
}

function getFallback(currentProducts, contact) {
  const remaining = ALL_PRODUCTS.filter(p => !currentProducts.some(cp => (cp || "").toLowerCase().includes(p.toLowerCase()) || p.toLowerCase().includes((cp || "").toLowerCase())));
  return remaining.slice(0, 5).map((p, i) => ({
    product: p,
    reason: `High fit for ${contact?.company || "this practice"}${contact?.specialty ? ` (${contact.specialty})` : ""}.`,
    match: 88 - (i * 9),
  }));
}
